import React, { useEffect, useCallback, useState, useRef } from 'react';
import '../App.css';
import GameChat from './GameChat';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import socket, { setGameContext, clearGameContext } from '../utils/socket';
import { openExternalUrl } from '../utils/openExternal';

// Extracted game components
import RulesModal from './game/RulesModal';
import FirstPlayerModal from './game/FirstPlayerModal';
import GameOverModal from './game/GameOverModal';
import DiceDisplay from './game/DiceDisplay';
import TurnControls from './game/TurnControls';
import PlayerList from './game/PlayerList';
import LiveScore from './game/LiveScore';
import OpponentScores from './game/OpponentScores';
import Scorecard from './game/Scorecard';
import AudioFeaturePopup from './game/AudioFeaturePopup';
import { AudioMuteButton } from './AudioSettings';

// Custom hooks
import { useGameState } from '../hooks/useGameState';
import useGameSounds from '../hooks/useGameSounds';

const GamePage = ({ gameId, playerName, gameType, gameData, onEndGame }) => {
  const { t } = useLanguage();
  const { playDiceRoll, playMarkNumber, playTurnChange, playTimerTick, stopTimerTick } = useGameSounds();

  // Ref for playDiceRoll to use in socket handlers (avoids stale closure)
  const playDiceRollRef = useRef(playDiceRoll);
  useEffect(() => {
    playDiceRollRef.current = playDiceRoll;
  }, [playDiceRoll]);

  // Ref to track previous dice values for detecting new dice rolls
  const previousDiceRef = useRef(null);

  // Initialize game state hook - replaces 30+ useState declarations
  const gameState = useGameState(gameId, playerName);

  // Initialize timer enabled from gameData when component mounts
  useEffect(() => {
    if (gameData?.turnTimerEnabled !== undefined) {
      gameState.setTurnTimerEnabled(gameData.turnTimerEnabled);
    }
  }, [gameData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize opponent scores visibility from gameData when component mounts
  useEffect(() => {
    console.log('[DEBUG] useEffect gameData.showOpponentScores:', gameData?.showOpponentScores);
    if (gameData?.showOpponentScores !== undefined) {
      console.log('[DEBUG] Initializing showOpponentScores from gameData prop:', gameData.showOpponentScores);
      gameState.setShowOpponentScores(gameData.showOpponentScores);
    }
  }, [gameData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Destructure all state and setters from the hook
  const {
    // Dice
    dice, setDice,
    selectedWhiteDie, setSelectedWhiteDie,
    selectedColor, setSelectedColor,

    // Scorecard
    scorecard, setScorecard,

    // Rows
    closedRows, setClosedRows,
    rowClosers, setRowClosers,
    activeDice, setActiveDice,
    pendingRowClosures, setPendingRowClosures,
    rowClosureOverlays, setRowClosureOverlays,

    // Players and turns
    currentPlayer, setCurrentPlayer,
    players, setPlayers,

    // Game flow
    gameStatus, setGameStatus,
    turnPhase, setTurnPhase,
    isLoading, setIsLoading,
    autoEndingTurn, setAutoEndingTurn,

    // Turn actions
    turnActions, setTurnActions,

    // UI
    message, setMessage,
    connectionError, setConnectionError,
    setShowGuidance,
    setGuidanceStep,

    // White phase
    playerWhitePhaseComplete, setPlayerWhitePhaseComplete,

    // Initialization
    gameInitialized, setGameInitialized,
    showFirstPlayerModal, setShowFirstPlayerModal,
    spinningWheel, setSpinningWheel,

    // Game end
    allPlayerScores, setAllPlayerScores,
    gameWinner, setGameWinner,
    allPlayerScorecards, setAllPlayerScorecards,
    allPlayerClosedRows, setAllPlayerClosedRows,
    allPlayerRowClosers, setAllPlayerRowClosers,

    // Scorecard zoom
    scorecardZoom, setScorecardZoom,

    // Modals
    showRulesModal, setShowRulesModal,

    // Chat
    showChat, setShowChat,
    unreadMessages, setUnreadMessages,

    // Facebook share
    shareStatus, setShareStatus,

    // Turn timer
    timerEndTime,
    setTimerEndTime,
    turnTimerEnabled,
    setTurnTimerEnabled,

    // Opponent scores visibility
    showOpponentScores,
    setShowOpponentScores,
    allPlayerLiveScores,
    setAllPlayerLiveScores,

    // Refs
    scorecardRef,
    loadingTimeoutRef,
    overlayTimeoutsRef
  } = gameState;

  // Timer countdown state (local to GamePage)
  const [timerSecondsLeft, setTimerSecondsLeft] = useState(null);

  // Timer countdown effect
  useEffect(() => {
    if (!timerEndTime) {
      setTimerSecondsLeft(null);
      return;
    }

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((timerEndTime - Date.now()) / 1000));
      setTimerSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
      }
    };

    // Initial update
    updateTimer();

    const interval = setInterval(updateTimer, 100);

    return () => clearInterval(interval);
  }, [timerEndTime]);

  // Sound: Timer tick for last 5 seconds, only if player hasn't marked yet
  useEffect(() => {
    if (timerSecondsLeft !== null && timerSecondsLeft <= 5 && timerSecondsLeft > 0) {
      // Only play tick if player hasn't completed their action for current phase
      const hasMarkedInCurrentPhase =
        (turnPhase === 'whiteSum' && (turnActions.whiteSumMarked || turnActions.whiteSumSkipped)) ||
        (turnPhase === 'colorDice' && turnActions.colorDiceMarked);

      if (!hasMarkedInCurrentPhase) {
        playTimerTick();
      }
    } else if (timerSecondsLeft === 0 || timerSecondsLeft === null) {
      // Stop the tick sound when timer ends
      stopTimerTick();
    }
  }, [timerSecondsLeft, turnPhase, turnActions, playTimerTick, stopTimerTick]);

  // Sound: Turn change when current player changes
  const prevCurrentPlayerRef = useRef(currentPlayer);
  useEffect(() => {
    if (currentPlayer && currentPlayer !== prevCurrentPlayerRef.current && gameStatus !== 'waiting') {
      playTurnChange();
    }
    prevCurrentPlayerRef.current = currentPlayer;
  }, [currentPlayer, gameStatus, playTurnChange]);

  // Sound: Mark number when scorecard changes
  const prevScorecardRef = useRef(scorecard);
  useEffect(() => {
    // Check if any row has gained a new mark
    const colors = ['red', 'yellow', 'green', 'blue'];
    const hasNewMark = colors.some(color =>
      scorecard[color].length > (prevScorecardRef.current[color]?.length || 0)
    );

    if (hasNewMark) {
      playMarkNumber();
    }
    prevScorecardRef.current = scorecard;
  }, [scorecard, playMarkNumber]);

  // Debug info for tracking state
  useEffect(() => {
    console.log('[DEBUG] Current turnActions state:', turnActions);
  }, [turnActions]);

  // Modified useEffect to handle first player selection properly
  useEffect(() => {
    // If we're the host and there are multiple players and game needs initialization
    const isHost = players.find(p => p.name === playerName)?.isHost;
    console.log(`Checking first player selection: isHost=${isHost}, players=${players.length}, gameInitialized=${gameInitialized}`);
    
    // ONLY the host should show the selection modal
    const needsFirstPlayerSelection = players.length > 1 && !gameInitialized && isHost === true;
    
    if (needsFirstPlayerSelection) {
      console.log('Setting up first player selection as host with multiple players');
      setShowFirstPlayerModal(true);
    } else {
      setShowFirstPlayerModal(false);
    }
  }, [players, playerName, gameInitialized, setShowFirstPlayerModal]);

  // Initialize socket connection and set up all event listeners in a single useEffect hook
  useEffect(() => {
    console.log('GamePage mounted, initializing socket connection...');
    
    // Use the shared socket instance
    if (socket) {
      // Set up event handlers when socket is available
      const setupSocketHandlers = () => {
        console.log('Game socket connected!');
        setConnectionError(false);
        setIsLoading(false);

        // Join the game room. The callback catches the case where the server no
        // longer knows this gameId (typically a refresh against an expired or
        // server-restarted game): clear the stale context and bail to the
        // homepage instead of leaving the user stranded on a frozen GamePage.
        console.log(`Joining game room for ${gameId} as ${playerName}`);
        socket.emit('joinGameRoom', { gameId, playerName }, (response) => {
          if (response && response.success === false && response.error === 'Game not found') {
            console.warn('joinGameRoom rejected: game not found, returning to home');
            clearGameContext();
            if (typeof onEndGame === 'function') onEndGame();
          }
        });

        // Set game context for automatic reconnection
        setGameContext(gameId, playerName, 'game');
      };
      
      // Set up handlers
      if (socket.connected) {
        setupSocketHandlers();
      }
      
      // Connect event handler
      socket.on('connect', setupSocketHandlers);
      
      // Connect error handler
      socket.on('connect_error', (err) => {
        console.error('Game socket connection error:', err);
        setConnectionError(true);
        setIsLoading(false);
        setMessage(`Connection error: Unable to connect to game server. Running in offline mode.`);
      });
      
      // Disconnect handler
      socket.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason);
        setMessage(`Connection lost: ${reason}. Attempting to reconnect...`);
      });
      
      // Handle player quit notification
      socket.on('playerQuit', (data) => {
        console.log('Player quit notification received:', data);
        if (data && data.playerName && data.playerName !== playerName) {
          // Only show message if it's not the current player (who is quitting)
          const quitMessage = t('messages.playerQuit', { playerName: data.playerName });
          setMessage(quitMessage);

          // Also add system message to chat if chat component is being used
          if (socket.connected) {
            socket.emit('chat:message', {
              gameId,
              playerName: 'System',
              message: quitMessage,
              isSystem: true
            });
          }

          // Make the notification more persistent - show for 10 seconds
          setTimeout(() => {
            setMessage('');
          }, 10000);
        }
      });
      
      // Listen for game updates
      socket.on('gameUpdate', (gameData) => {
        console.log('Game update received:', gameData);
        if (gameData) {
          setIsLoading(false);

          // Check if dice were just rolled (detect by value change, play sound for non-rollers)
          if (gameData.diceValues && gameData.currentPlayer && gameData.currentPlayer !== playerName) {
            const prevDice = previousDiceRef.current;
            const newDice = gameData.diceValues;

            // Detect new dice roll: new dice have actual values AND values changed
            // This prevents triggering when dice are reset to null (on turn change/game start)
            const hasNewDiceValues = newDice.white1 !== null && newDice.white2 !== null;
            const isDiceRoll = hasNewDiceValues && (
              !prevDice ||
              prevDice.white1 !== newDice.white1 ||
              prevDice.white2 !== newDice.white2
            );

            if (isDiceRoll) {
              console.log('[DEBUG] Dice roll detected via gameUpdate, playing sound for:', gameData.currentPlayer);
              playDiceRollRef.current();
            }
          }

          // Update previous dice ref for next comparison
          if (gameData.diceValues) {
            previousDiceRef.current = { ...gameData.diceValues };
          }

          // Process players data with consistent structure
          if (gameData.players && Array.isArray(gameData.players)) {
            const updatedPlayers = gameData.players.map(p => {
              // Handle string-only format (legacy)
              if (typeof p === 'string') {
                return {
                  name: p,
                  isHost: gameData.players.indexOf(p) === 0 // First player is host
                };
              }
              // Ensure we have proper player objects with correct host status
              return {
                name: p.name,
                isHost: Boolean(p.isHost), // Preserve the server's host designation
                isReady: Boolean(p.isReady)
              };
            });
            
            console.log('Updated players list from gameUpdate with proper host status:', updatedPlayers);
            setPlayers(updatedPlayers);

            // Check if we need to show first player selection - ONLY FOR HOST
            const isHost = updatedPlayers.find(p => p.name === playerName)?.isHost;
            if (isHost === true) {
              const needsFirstPlayerSelection = updatedPlayers.length > 1 && !gameData.initialized;
              
              if (needsFirstPlayerSelection) {
                console.log('Host showing first player selection modal');
                setShowFirstPlayerModal(true);
              } else {
                setShowFirstPlayerModal(false);
              }
            } else {
              // Non-hosts should never see the modal
              setShowFirstPlayerModal(false);
            }
          }
          
          if (gameData.currentPlayer) setCurrentPlayer(gameData.currentPlayer);
          if (gameData.diceValues) setDice(gameData.diceValues);
          if (gameData.status) {
            // Once finished, ignore late gameUpdate broadcasts (e.g. from joinGameRoom
            // rejoins on app-foreground/focus, or the Android visibility polling loop)
            // that would otherwise drop the user back to the in-game view from the
            // game-over modal. gameRestarted / handleStartNewGame call setGameStatus
            // directly with a value, so they bypass this guard and still work.
            setGameStatus(prev => (prev === 'finished' ? 'finished' : gameData.status));
          }
          if (gameData.turnPhase) setTurnPhase(gameData.turnPhase);
          if (gameData.initialized !== undefined) setGameInitialized(gameData.initialized);
          if (gameData.turnTimerEnabled !== undefined) setTurnTimerEnabled(gameData.turnTimerEnabled);
          if (gameData.showOpponentScores !== undefined) {
            console.log('[DEBUG] Setting showOpponentScores from gameUpdate:', gameData.showOpponentScores);
            setShowOpponentScores(gameData.showOpponentScores);
          }
          if (gameData.allPlayerLiveScores) {
            console.log('[DEBUG] Setting allPlayerLiveScores from gameUpdate:', gameData.allPlayerLiveScores);
            setAllPlayerLiveScores(gameData.allPlayerLiveScores);
          }

          // Update current player's scorecard
          if (gameData.scorecard && gameData.scorecard[playerName]) {
            setScorecard(gameData.scorecard[playerName]);
          }
        } else {
          console.warn('Received null or undefined gameUpdate data');
        }
      });
      
      // Listen for the first player selected event
      socket.on('firstPlayerSelected', (data) => {
        console.log('First player selected event received:', data);
        if (data && data.player) {
          setCurrentPlayer(data.player);
          setTurnPhase('initial');
          // Reset all dice
          if (data.diceValues) {
            setDice(data.diceValues);
          }
          
          // Use translation for message if messageKey is provided
          if (data.messageKey) {
            setMessage(t(`firstPlayerSelection.${data.messageKey}`, data.messageData || {}));
          } else if (data.message) {
            // Legacy support
            setMessage(data.message);
          }
          
          // Update game status
          setGameStatus('active');
          
          // If I'm the selected first player, show guidance for rolling
          if (data.player === playerName) {
            setGuidanceStep('initial');
            setShowGuidance(true);
          }
      }
    });

    // Listen for row closed events to update the closed rows and active dice for all players
    socket.on('rowClosed', (data) => {
      console.log('[DEBUG] Received rowClosed event:', data);

      if (data.closedRows) {
        console.log('[DEBUG] Updating closedRows state from server data:', data.closedRows);
        setClosedRows(data.closedRows);
      }

      // Update rowClosers state if provided
      if (data.rowClosers) {
        console.log('[DEBUG] Updating rowClosers state from server data:', data.rowClosers);
        setRowClosers(data.rowClosers);
      }

      if (data.closedRows) {
        
        // Add animation ONLY for the player who closed the row
        setTimeout(() => {
          // First, get the specific row that was just closed
          const justClosedRow = data.row; // Get the specific row from the event data
          
          // Animation for the just closed row
          if (justClosedRow && data.closedRows[justClosedRow]) {
            // Get the last number for this row
            const lastNumber = justClosedRow === 'red' || justClosedRow === 'yellow' ? 12 : 2;
            
            // ONLY cross out the lock symbol for the player who closed the row
            if (data.playerName === playerName) {
              const lockElement = document.querySelector(`.row.${justClosedRow} .number.lock`);
              if (lockElement) {
                // First ensure correct classes are applied - use 'crossed' instead of 'marked'
                lockElement.classList.add('crossed');
                
                // Remove any pending classes
                lockElement.classList.remove('pending-marked');
                
                // Then add the marking animation for visual feedback
                lockElement.classList.add('marking');
                setTimeout(() => {
                  lockElement.classList.remove('marking');
                }, 300);
              }
              
              // ONLY mark the last number in the row if it's the player who closed the row
              const lastNumberElement = document.querySelector(`.row.${justClosedRow} .number:nth-child(${getNumberIndex(justClosedRow, lastNumber)})`);
              if (lastNumberElement) {
                // Ensure it appears as marked
                lastNumberElement.classList.add('marked');
                
                // Add animation
                lastNumberElement.classList.add('marking');
                setTimeout(() => {
                  lastNumberElement.classList.remove('marking');
                }, 300);
              }
            }
            
            // Clear any existing overlays and their timeouts
            Object.keys(overlayTimeoutsRef.current).forEach(row => {
              if (overlayTimeoutsRef.current[row]) {
                clearTimeout(overlayTimeoutsRef.current[row]);
                overlayTimeoutsRef.current[row] = null;
              }
            });
            
            // Reset all overlays before showing the new one
            setRowClosureOverlays({
              red: false,
              yellow: false,
              green: false,
              blue: false
            });
            
            // After a small delay to ensure the state is reset, show the new overlay for the just closed row
            setTimeout(() => {
              // Show row closure overlay for ALL players
              setRowClosureOverlays(prev => ({
                ...prev,
                [justClosedRow]: true
              }));
              
              // Automatically hide the overlay after a few seconds
              overlayTimeoutsRef.current[justClosedRow] = setTimeout(() => {
                setRowClosureOverlays(prev => ({
                  ...prev,
                  [justClosedRow]: false
                }));
              }, 2500);
            }, 50);
          }
        }, 300);
        
        // Hide active dice for closed rows 
        setActiveDice(data.activeDice);
      }
      
      // Set additional data for the scorecard if available
      if (data.scorecard && data.scorecard[playerName]) {
        setScorecard(prevScorecard => ({
          ...prevScorecard,
          ...data.scorecard[playerName]
        }));
      }
    });

    // Handle turn phase updates
    socket.on('turnPhaseUpdate', (data) => {
      console.log('[DEBUG] Received turnPhaseUpdate:', data);
      console.log('[DEBUG] Current turnActions before processing:', JSON.stringify(turnActions));
      console.log('[DEBUG] Current playerWhitePhaseComplete before processing:', playerWhitePhaseComplete);
      console.log('[DEBUG] Current pendingRowClosures:', pendingRowClosures);
      
      // First update the turn phase state
      if (data.turnPhase) {
        // Check for phase transition from whiteSum to colorDice to finalize pending row closures
        if (turnPhase === 'whiteSum' && data.turnPhase === 'colorDice') {
          console.log('[DEBUG] Phase changing from whiteSum to colorDice - finalizing pending row closures');
          
          // Using immediate call rather than setTimeout to ensure synchronous execution
          // This fixes timing issues with row closure finalization
          finalizePendingRowClosures();
          
          // Also ensure our turn actions are properly set based on our white phase completion status
          if (playerWhitePhaseComplete[playerName] === true) {
            // If we've completed the white phase but our turnActions state doesn't show it,
            // fix that to ensure proper button rendering
            if (!turnActions.whiteSumMarked && !turnActions.whiteSumSkipped) {
              console.log('[DEBUG] Fixing inconsistent turn actions on phase change');
              setTurnActions(prev => {
                // Check if we've marked anything in this turn to determine how we completed white phase
                const hasMarkedWhiteSum = Object.values(data.whiteDiceMarked || {}).includes(playerName);
                
                return {
                  ...prev,
                  whiteSumMarked: hasMarkedWhiteSum,
                  whiteSumSkipped: !hasMarkedWhiteSum
                };
              });
            }
          }
        }
        
        // Update the phase state
        setTurnPhase(data.turnPhase);
      }
      
      // If changing to whiteSum phase, reset turn actions and white phase tracking
      if (data.turnPhase === 'whiteSum') {
        console.log('[DEBUG] Resetting turn actions and white phase completion for whiteSum phase');
        
        // Also reset pending row closures when entering a new white phase
        setPendingRowClosures({
          red: false,
          yellow: false,
          green: false,
          blue: false
        });
        
        setTurnActions({
          whiteSumMarked: false,
          colorDiceMarked: false,
          whiteSumSkipped: false
        });
        
        // Also reset player white phase completion tracking
        setPlayerWhitePhaseComplete({});
      }
      
      if (data.currentPlayer) {
        setCurrentPlayer(data.currentPlayer);
      }

      // Animate dice for rolls made by other players/bots. The local roller already
      // ran this animation in handleRollDice; here we give bot (and opponent) rolls
      // the same "rolling" shake + sound instead of the result appearing instantly.
      if (data.messageKey === 'playerRolledDice' && data.currentPlayer && data.currentPlayer !== playerName) {
        playDiceRollRef.current();
        const diceElements = document.querySelectorAll('.die');
        diceElements.forEach(die => {
          die.classList.add('rolling');
          setTimeout(() => die.classList.remove('rolling'), 600);
        });
      }

        // Check if completedPlayers was sent and update whitephase completion
        if (data.completedPlayers) {
          console.log('[DEBUG] Updating playerWhitePhaseComplete from turnPhaseUpdate:', data.completedPlayers);
          setPlayerWhitePhaseComplete(data.completedPlayers);
        }
      
      // If server sends a messageKey, use it for translation
      if (data.messageKey) {
        // Use the client-side translation with the provided data
        const messageData = { ...data.messageData } || {};
        
        // Special handling for phase translation
        if (data.messageKey === 'phaseChanged' && messageData.phase) {
          // Translate the phase name
          messageData.phase = t(`phases.${messageData.phase}`);
        }
        
        // Special handling for the current player - add differentiation for yourTurnToRoll
        if (data.messageKey === 'yourTurnToRoll') {
          if (data.currentPlayer === playerName) {
            setMessage(t('messages.yourTurnToRoll'));
          } else {
            setMessage(t('messages.turnComplete', { player: data.currentPlayer }));
          }
        } else {
          setMessage(t(`messages.${data.messageKey}`, messageData));
        }
      }
      // Legacy support for direct messages
      else if (data.message) {
          setMessage(data.message);
        }
      });
      
      // Player completed white phase event
      socket.on('playerCompletedWhitePhase', (data) => {
        console.log('[DEBUG] Received playerCompletedWhitePhase event:', data);
        
        if (data.playerName && data.completed !== undefined) {
          // Update player white phase completion state
          setPlayerWhitePhaseComplete(prev => ({
            ...prev,
            [data.playerName]: data.completed
          }));
          
          // If this is about the current player, update turnActions to match
          if (data.playerName === playerName) {
            console.log('[DEBUG] Updating local turnActions based on white phase completion');
            
            // If we receive word from the server that we've completed the white phase,
            // update our local state to match
            if (data.action === 'skip') {
              setTurnActions(prev => ({
                ...prev,
                whiteSumSkipped: true,
                whiteSumMarked: false
              }));
            } else if (data.action === 'mark') {
              setTurnActions(prev => ({
                ...prev,
                whiteSumSkipped: false,
                whiteSumMarked: true
              }));
            }
          }
        }
      });
      
      // White phase status update from server
      socket.on('whitePhaseStatus', (data) => {
      console.log('[DEBUG] Received whitePhaseStatus:', data);
      console.log('[DEBUG] Current playerWhitePhaseComplete before update:', playerWhitePhaseComplete);
      
          // Update the local white phase completion tracking
      if (data.completedPlayers) {
        console.log('[DEBUG] Updating playerWhitePhaseComplete from server data:', data.completedPlayers);
        setPlayerWhitePhaseComplete(data.completedPlayers);
      } else {
        console.log('[DEBUG] No completedPlayers data in whitePhaseStatus event');
      }
      
      // Update the local whiteSumMarked/whiteSumSkipped status
      if (data.whiteDiceMarked) {
        console.log('[DEBUG] whiteDiceMarked data received:', data.whiteDiceMarked);
        // If server says this player marked white dice
        if (data.whiteDiceMarked[playerName]) {
          console.log('[DEBUG] Server indicates player marked white dice');
            setTurnActions(prev => ({
              ...prev,
            whiteSumMarked: true,
            whiteSumSkipped: false
        }));
      }
        // If server says player completed (in completedPlayers) but didn't mark (not in whiteDiceMarked)
        else if (data.completedPlayers && 
                data.completedPlayers[playerName] && 
                !data.whiteDiceMarked[playerName]) {
          console.log('[DEBUG] Server indicates player completed white phase but didn\'t mark dice (skipped)');
          setTurnActions(prev => ({
            ...prev,
            whiteSumMarked: false,
            whiteSumSkipped: true
          }));
        }
      } else {
        console.log('[DEBUG] No whiteDiceMarked data in whitePhaseStatus event');
        }
      });
      
      // Game end event
    socket.on('gameEnd', (data) => {
      console.log('Game ended event received:', data);
      
      // First, clear any existing active row closure overlays
      setRowClosureOverlays({
        red: false,
        yellow: false,
        green: false,
        blue: false
      });
      
      // Clear any pending overlay timeouts
      Object.keys(overlayTimeoutsRef.current).forEach(row => {
        if (overlayTimeoutsRef.current[row]) {
          clearTimeout(overlayTimeoutsRef.current[row]);
          overlayTimeoutsRef.current[row] = null;
        }
      });
      
      // If the game ended due to a row closure, show that overlay first
      if (data.reason === 'rowsClosed' && data.closedRow) {
        console.log(`[DEBUG] Showing overlay for final closed row: ${data.closedRow}`);
        
        // Show the overlay for the row that triggered the game end
        const newRowClosureOverlays = { ...rowClosureOverlays };
        newRowClosureOverlays[data.closedRow] = true;
        setRowClosureOverlays(newRowClosureOverlays);
        
        // Set a timeout to transition to game over after showing the row closure
        setTimeout(() => {
          // Now proceed with game over transition
          setGameStatus('finished');
          
          // Clear the row closure overlay
          setRowClosureOverlays({
            red: false,
            yellow: false,
            green: false,
            blue: false
          });
          
          processGameEndData(data);
        }, 2000); // Wait 2 seconds before showing game over screen
      } else {
        // For other game end reasons (penalties), transition immediately
        setGameStatus('finished');
        processGameEndData(data);
      }
    });
    
    // Helper function to process game end data
    const processGameEndData = (data) => {
      if (data.allScores) {
        setAllPlayerScores(data.allScores);
      }
      
      if (data.winner) {
        setGameWinner(data.winner);
      }
      
      // Display a more specific message based on the reason for game ending
      if (data.reason === 'penalties') {
        // Game ended because a player reached 4 penalties
        setMessage(t('gameOver.penalties', { player: data.player }));
      } else if (data.reason === 'rowsClosed') {
        // Game ended because two rows were closed
        // Check if the data tells us who closed the second row
        if (data.player && data.closedRow) {
          // If we have the specific row information, use it
          const closedRowColor = t(`colors.${data.closedRow}`);
          
          if (data.previousClosedRow) {
            // If we know both rows that were closed, include them in the message
            const previousRowColor = t(`colors.${data.previousClosedRow}`);
            setMessage(t('gameOver.rowsClosedByPlayerWithRows', { 
              player: data.player,
              row: closedRowColor,
              previousRow: previousRowColor
            }));
          } else {
            // Otherwise just mention the row that was closed last
            setMessage(t('gameOver.rowsClosedByPlayerWithRow', { 
              player: data.player,
              row: closedRowColor
            }));
          }
        } else if (data.player) {
          // Fall back to generic message
          setMessage(t('gameOver.rowsClosedByPlayer', { player: data.player }));
        } else {
          setMessage(t('gameOver.rowsClosed'));
        }
      } else if (data.message) {
        // Fallback to server-provided message if available
        setMessage(data.message);
      } else {
        // Generic fallback
        setMessage(t('gameOver.defaultMessage'));
      }
      
      // Store the complete scorecards received directly
      if (data.allScorecards) {
        setAllPlayerScorecards(data.allScorecards);
      }
      
      // Store closed rows information for all players
      if (data.allClosedRows) {
        setAllPlayerClosedRows(data.allClosedRows);
      } else {
        // If no closed rows data from server, infer from scorecards
        const inferredClosedRows = {};

        if (data.allScorecards) {
          Object.entries(data.allScorecards).forEach(([player, playerScorecard]) => {
            inferredClosedRows[player] = {
              red: playerScorecard.red.includes(12) && playerScorecard.red.length >= 5,
              yellow: playerScorecard.yellow.includes(12) && playerScorecard.yellow.length >= 5,
              green: playerScorecard.green.includes(2) && playerScorecard.green.length >= 5,
              blue: playerScorecard.blue.includes(2) && playerScorecard.blue.length >= 5
            };
          });
        }

        setAllPlayerClosedRows(inferredClosedRows);
      }

      // Infer which players closed which rows (for lock bonus calculation)
      // A player closed a row if they have the last number marked AND >= 6 marks total
      if (data.allScorecards) {
        const inferredRowClosers = {
          red: [],
          yellow: [],
          green: [],
          blue: []
        };

        Object.entries(data.allScorecards).forEach(([player, playerScorecard]) => {
          // Check red row (last number is 12)
          if (playerScorecard.red.includes(12) && playerScorecard.red.length >= 6) {
            inferredRowClosers.red.push(player);
          }
          // Check yellow row (last number is 12)
          if (playerScorecard.yellow.includes(12) && playerScorecard.yellow.length >= 6) {
            inferredRowClosers.yellow.push(player);
          }
          // Check green row (last number is 2)
          if (playerScorecard.green.includes(2) && playerScorecard.green.length >= 6) {
            inferredRowClosers.green.push(player);
          }
          // Check blue row (last number is 2)
          if (playerScorecard.blue.includes(2) && playerScorecard.blue.length >= 6) {
            inferredRowClosers.blue.push(player);
          }
        });

        console.log('[DEBUG] Inferred rowClosers for all players:', inferredRowClosers);
        setAllPlayerRowClosers(inferredRowClosers);
      }
    };

      // Game restarted event
    socket.on('gameRestarted', (data) => {
        console.log('Game restarted event received:', data);
      
        // Reset all game state
        setGameStatus('waiting');
      setTurnPhase('initial');
        setGameInitialized(false);
        
        // Reset scores and game state
        setScorecard({
          red: [],
          yellow: [],
          green: [],
          blue: [],
          penalties: 0
        });
        
      setClosedRows({
        red: false,
        yellow: false,
        green: false,
        blue: false
      });
        
      setActiveDice({
        red: true,
        yellow: true,
        green: true,
        blue: true
      });
      
      // Reset row closure overlays
      setRowClosureOverlays({
        red: false,
        yellow: false,
        green: false,
        blue: false
      });
      
      // Clear any active overlay timeouts
      Object.keys(overlayTimeoutsRef.current).forEach(row => {
        if (overlayTimeoutsRef.current[row]) {
          clearTimeout(overlayTimeoutsRef.current[row]);
          overlayTimeoutsRef.current[row] = null;
        }
      });
        
        // Reset turn actions
        setTurnActions({
          whiteSumMarked: false,
          colorDiceMarked: false,
          whiteSumSkipped: false
        });
        
        // Reset dice values
        setDice({
          white1: null,
          white2: null,
          red: null,
          yellow: null,
          green: null,
          blue: null
        });

        // Reset previous dice ref for sound detection
        previousDiceRef.current = null;

        // Reset player white phase completion
        setPlayerWhitePhaseComplete({});
        
        // Update players list if provided
        if (data.players) {
          setPlayers(data.players);
          
          // Check if we're the host and need to show first player selection
          const isHost = data.players.find(p => p.name === playerName)?.isHost;
          if (isHost && data.players.length > 1) {
            console.log('Setting up first player selection after game restart');
            setShowFirstPlayerModal(true);
          } else {
            setShowFirstPlayerModal(false);
          }
        }
        
        // Display restart message
        if (data.message) {
          setMessage(data.message);
        } else {
          setMessage('Game has been restarted. Host will select who goes first.');
        }
      });
      
      // Dice rolled event
      socket.on('diceRolled', (data) => {
        console.log('Dice rolled event received:', data);

        if (data.diceValues) {
          setDice(data.diceValues);

          // Play dice roll sound for non-active players
          // Active player already heard it in handleRollDice
          if (data.currentPlayer !== playerName) {
            playDiceRollRef.current();
          }
        }

        if (data.turnPhase) {
          setTurnPhase(data.turnPhase);
        }

        if (data.message) {
          setMessage(data.message);
        }
      });
      
      // Number marked event
      socket.on('numberMarked', (data) => {
        console.log('Number marked event received:', data);
        
        if (data.scorecard && data.scorecard[playerName]) {
          setScorecard(data.scorecard[playerName]);
        }
        
        if (data.turnPhase) {
          setTurnPhase(data.turnPhase);
        }
        
        if (data.diceValues) {
          setDice(data.diceValues);
        }
        
        if (data.message) {
          setMessage(data.message);
        }
      });
      
      // Error events
      socket.on('error', (errorData) => {
        // Filter out game validation messages that aren't critical errors
        const validationMessages = [
          'Not your turn to end',
          'Not your turn to roll',
          'Not your turn to mark'
        ];

        const errorMessage = typeof errorData === 'string' ? errorData : errorData?.message;
        const isValidation = validationMessages.some(msg => errorMessage?.includes(msg));

        if (isValidation) {
          // Log as warning instead of error - these are just game rules being enforced
          console.warn('Game validation:', errorMessage);
          // Don't show these in the UI - they're expected behavior
        } else {
          // Real errors - log and show to user
          console.error('Socket error event:', errorData);
          console.error('Error type:', typeof errorData);
          console.error('Error details:', JSON.stringify(errorData, null, 2));

          if (typeof errorData === 'string') {
            setMessage(`Error: ${errorData}`);
          } else if (errorData && errorData.message) {
            setMessage(`Error: ${errorData.message}`);
          } else {
            setMessage('An unknown error occurred');
          }
        }
      });
      
      // Listen for whiteDiceMarked events
      socket.on('whiteDiceMarked', (data) => {
        console.log('Received whiteDiceMarked event:', data);
        
        // If this event is about the current player
        if (data.playerName === playerName) {
          console.log('[DEBUG] Setting whiteSumMarked=true for this player based on server event');
          
          setTurnActions(prev => {
            // Only update if not already true to avoid unnecessary re-renders
            if (prev.whiteSumMarked) {
              console.log('[DEBUG] whiteSumMarked already true, not updating');
              return prev;
            }
            
            const newState = {
              ...prev,
              whiteSumMarked: true,
              whiteSumSkipped: false
            };
            console.log('[DEBUG] New turnActions state after whiteDiceMarked:', newState);
            return newState;
          });
          
          // Update local white phase completion tracking
          setPlayerWhitePhaseComplete(prev => ({
            ...prev,
            [playerName]: true
          }));
        }
        
        // If the server sent a whiteDiceMarked object with all players' status
        if (data.whiteDiceMarked) {
          // Update local tracking based on server data
          console.log('[DEBUG] Updating playerWhitePhaseComplete based on server whiteDiceMarked data');
          const updatedCompletion = { ...playerWhitePhaseComplete };
          
          // Mark all players who marked white dice as completed
          Object.keys(data.whiteDiceMarked).forEach(player => {
            if (data.whiteDiceMarked[player]) {
              updatedCompletion[player] = true;
            }
          });
          
          setPlayerWhitePhaseComplete(updatedCompletion);
        }
      });
      
      // Turn complete event handler
      socket.on('turnComplete', (data) => {
        console.log('[DEBUG] Received turnComplete event:', data);
        
        // Reset the auto-ending flag to ensure it's cleared in case the auto-end timeout didn't finish
        setAutoEndingTurn(false);
        
        // Reset the turn actions for the next turn
        setTurnActions({
          whiteSumMarked: false,
          colorDiceMarked: false,
          whiteSumSkipped: false
        });
        
        // Reset player white phase completion tracking
        setPlayerWhitePhaseComplete({});
        
        // Process the data
        if (data.nextPlayer) {
          setCurrentPlayer(data.nextPlayer);
        }
        
        if (data.message) {
          setMessage(data.message);
        }
        
        if (data.diceValues) {
          setDice(data.diceValues);
        }
      });

      // Turn timer socket listeners
      socket.on('turnTimerUpdate', ({ endTime, duration, phase }) => {
        console.log('[DEBUG] Turn timer update:', { endTime, duration, phase });
        setTimerEndTime(endTime);
      });

      socket.on('turnTimerExpired', ({ phase }) => {
        console.log('[DEBUG] Turn timer expired:', { phase });
        setTimerEndTime(null);
      });

      socket.on('autoPenalty', (data) => {
        console.log('[DEBUG] Auto penalty applied:', data);
        if (data.playerName === playerName) {
          setMessage(t('messages.autoPenalty'));
          // Update local scorecard penalty count
          setScorecard(prev => ({
            ...prev,
            penalties: data.penalties
          }));
        } else {
          setMessage(t('messages.autoPenaltyOther', { player: data.playerName }));
        }
      });

      socket.on('turnTimerStopped', () => {
        console.log('[DEBUG] Turn timer stopped');
        setTimerEndTime(null);
      });

      socket.on('turnEnded', (data) => {
        console.log('[DEBUG] Turn ended:', data);
        setTimerEndTime(null);
        if (data.nextPlayer) {
          setCurrentPlayer(data.nextPlayer);
        }
        if (data.turnPhase) {
          setTurnPhase(data.turnPhase);
        }
        if (data.diceValues) {
          setDice(data.diceValues);
        }
        // Reset turn actions for new turn
        setTurnActions({
          whiteSumMarked: false,
          colorDiceMarked: false,
          whiteSumSkipped: false
        });
        setPlayerWhitePhaseComplete({});
        // Clear dice selection when turn ends (fixes timer expiry leaving dice selected)
        setSelectedWhiteDie(null);
        setSelectedColor(null);
      });
    }

    // Ensure loading state times out to prevent white screen
    loadingTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
    }, 5000); // Give up after 5 seconds to prevent permanent white screen

    // Clean up event listeners when the component unmounts
    return () => {
      if (socket) {
        console.log('Cleaning up socket event listeners');
        socket.off('connect');
        socket.off('disconnect');
        socket.off('gameUpdate');
        socket.off('playerQuit'); // Add cleanup for the playerQuit event
        socket.off('diceRolled');
        socket.off('turnPhaseUpdate');
        socket.off('markNumberResult');
        socket.off('skipWhiteDiceResult');
        socket.off('endTurnResult');
        socket.off('rowClosed');
        socket.off('firstPlayerSelected');
        socket.off('gameOver');
        socket.off('takePenaltyResult');
        socket.off('turnTimerUpdate');
        socket.off('turnTimerExpired');
        socket.off('autoPenalty');
        socket.off('turnTimerStopped');
        socket.off('turnEnded');
      }
      // Note: Don't clear game context on unmount - needed for reconnection after app backgrounding
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, playerName, t]);

  // Function to roll the dice
  const handleRollDice = () => {
    // Only the active player can roll the dice
    if (currentPlayer !== playerName) {
      setMessage(t('messages.activePlayerOnly', { action: t('phases.initial').toLowerCase() }));
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Ensure first player selection has been made
    if (!gameInitialized) {
      setMessage(t('messages.waitForFirstPlayer'));
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Can only roll dice in the initial phase
    if (turnPhase !== 'initial') {
      setMessage(t('messages.initialPhaseOnly'));
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Set rolling animation
    setGameStatus('rolling');

    // Play dice roll sound
    playDiceRoll();

    // Add rolling animation class to dice
    const diceElements = document.querySelectorAll('.die');
    diceElements.forEach(die => {
      die.classList.add('rolling');
      setTimeout(() => {
        die.classList.remove('rolling');
      }, 500);
    });
    
    // Reset turn actions for new turn for all players by broadcasting
    console.log('[DEBUG] Resetting turn actions for new dice roll');
    const newTurnActions = {
      whiteSumMarked: false,
      colorDiceMarked: false,
      whiteSumSkipped: false
    };
    setTurnActions(newTurnActions);
    
    // Also reset player white phase completion tracking
    setPlayerWhitePhaseComplete({});
    
    if (socket.connected) {
      // First, roll the dice
      socket.emit('rollDice', { gameId, playerName });
      
      // After rolling, move to the whiteSum phase for ALL players in the game
      setTimeout(() => {
        setGameStatus('active');
        setTurnPhase('whiteSum');
        
        // Show guidance for white dice sum marking
        setGuidanceStep('whiteSum');
        setShowGuidance(true);
        
        // Explicitly broadcast to all players that we're now in the whiteSum phase
        // This ensures all players can mark using the white sum
        socket.emit('broadcastTurnPhase', { 
          gameId, 
          turnPhase: 'whiteSum', 
          currentPlayer: playerName,
          messageKey: 'playerRolledDice', // Use message key instead of translated text
          messageData: { player: playerName } // Send player name as data
        });
      }, 600);
    } else {
      // Fallback for offline mode
      setTimeout(() => {
        // Generate random values for the dice
        const randomDice = {
          white1: Math.floor(Math.random() * 6) + 1,
          white2: Math.floor(Math.random() * 6) + 1,
          red: Math.floor(Math.random() * 6) + 1,
          yellow: Math.floor(Math.random() * 6) + 1,
          green: Math.floor(Math.random() * 6) + 1,
          blue: Math.floor(Math.random() * 6) + 1
        };
        
        // Only include active dice
        Object.keys(randomDice).forEach(color => {
          if (color !== 'white1' && color !== 'white2' && !activeDice[color]) {
            randomDice[color] = 0;
          }
        });
        
        setDice(randomDice);
        setGameStatus('active');
        setTurnPhase('whiteSum');
        
        // Show guidance for white dice sum marking
        setGuidanceStep('whiteSum');
        setShowGuidance(true);
        
        // Set a message for offline mode
        const whiteSum = randomDice.white1 + randomDice.white2;
        setMessage(t('messages.youRolledDice', { sum: whiteSum }));
      }, 600);
    }
  };

  // Function to mark a number on the scorecard
  const handleMarkNumber = (row, number) => {
    // Can't mark if game is waiting or finished
    if (gameStatus === 'waiting' || gameStatus === 'finished') return;
    
    // Check if the row is closed - can't mark in a closed row
    if (closedRows[row]) {
      setMessage(`The ${row} row is closed and cannot be marked anymore`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Handle penalties - prevent direct marking through scorecard
    if (row === 'penalty') {
      setMessage('Penalties can only be marked using the penalty button');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Check for rightmost number rule: can only mark the rightmost number if already have 5 numbers
    // The rightmost number is 12 for red/yellow and 2 for green/blue
    const isRightmostNumber = (row === 'red' || row === 'yellow') ? number === 12 : number === 2;
    if (isRightmostNumber && scorecard[row].length < 5) {
      setMessage(t('messages.rightmostNumberRule', { color: t(`colors.${row}`) }));
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    console.log(`Attempting to mark ${number} in ${row} row. Current phase: ${turnPhase}. Current player: ${currentPlayer}. Your name: ${playerName}`);
    
    // Rules for marking in color rows
    if (['red', 'yellow', 'green', 'blue'].includes(row)) {
      // For white sum (all players can mark)
      if (turnPhase === 'whiteSum') {
        // Check if player has already marked using white dice this turn
        if (turnActions.whiteSumMarked) {
          setMessage('You already marked a number using white dice this turn');
          setTimeout(() => setMessage(''), 3000);
          return;
        }
        
        // Check if player has already skipped marking
        if (turnActions.whiteSumSkipped) {
          setMessage('You already skipped marking the white dice sum this turn');
          setTimeout(() => setMessage(''), 3000);
          return;
        }
        
        // Check if the number matches the white dice sum
        const whiteSum = dice.white1 + dice.white2;
        if (number !== whiteSum) {
          setMessage(`The white dice sum is ${whiteSum}, not ${number}`);
          setTimeout(() => setMessage(''), 3000);
          return;
        }
        
        // Check directional rules (left-to-right for red/yellow, right-to-left for green/blue)
        const isValid = isValidNumberPlacement(row, number);
        if (!isValid) {
          setMessage(`You cannot mark ${number} in the ${row} row due to sequence rules`);
          setTimeout(() => setMessage(''), 3000);
          return;
        }
        
        console.log(`Marking ${number} in ${row} row as part of white sum phase`);
        
        // Find the element being marked and add animation
        const numberElement = document.querySelector(`.row.${row} .number:nth-child(${getNumberIndex(row, number)})`);
        if (numberElement) {
          numberElement.classList.add('marking');
          setTimeout(() => numberElement.classList.remove('marking'), 300);
        }
        
        // All checks passed, mark the number
        const newScorecard = { ...scorecard };
        if (!newScorecard[row].includes(number)) {
          newScorecard[row] = [...newScorecard[row], number].sort((a, b) => {
            return row === 'red' || row === 'yellow' ? a - b : b - a;
          });
        }
        setScorecard(newScorecard);
        
        // Update turn actions - only for this player
        setTurnActions(prev => {
          const newState = { ...prev, whiteSumMarked: true, whiteSumSkipped: false };
          console.log('[DEBUG] Setting whiteSumMarked=true after marking with white dice:', newState);
          return newState;
        });
        
        if (socket.connected) {
          // Tell server about marking and broadcast to all players
          socket.emit('markNumber', { 
            gameId, 
            playerName, 
            row, 
            number, 
            actionType: 'whiteSum' 
          });
          
          // Broadcast that this player has marked using white dice
          socket.emit('whiteDiceMarked', {
            gameId,
            playerName,
            row,
            number
          });
          
          // Tell server we've completed our white phase (for both active and non-active players)
          socket.emit('playerCompletedWhitePhase', { 
            gameId, 
            playerName,
            action: 'mark'
          });
          
          // Update local tracking
          setPlayerWhitePhaseComplete(prev => ({
            ...prev,
            [playerName]: true
          }));
        }
        
        // Check if this was the last number in the row and should be closed
        // Pass 'whiteSum' as the action type and the updated scorecard
        checkRowClosure(row, newScorecard[row], 'whiteSum', newScorecard);
        
        // If active player marked a number using white sum, DON'T immediately move to color dice phase
        // Instead, wait for server to notify when all players have completed white phase
        if (currentPlayer === playerName) {
          // Don't change phase here, server will handle phase transition when all players are done
          setMessage(t('messages.activePlayerWhiteChoice'));
          
          // No longer emitting broadcastTurnPhase event here - server will handle phase transition
        } else {
          // Non-active player has completed their white dice action
          setMessage(t('messages.nonActiveMarked'));
        }
        
        return;
      }
      
      // For color dice (only active player)
      if (turnPhase === 'colorDice') {
        // Only the active player can mark using color dice
        if (currentPlayer !== playerName) {
          setMessage(t('messages.activePlayerOnly', { action: t('phases.colorDice').toLowerCase() }));
          setTimeout(() => setMessage(''), 3000);
          return;
        }
        
        // Check if player already marked with color dice
        if (turnActions.colorDiceMarked) {
          setMessage(t('messages.alreadyMarked'));
          setTimeout(() => setMessage(''), 3000);
          return;
        }
        
        // Calculate possible combinations - ensure all values are numbers
        const colorDieValue = Number(dice[row]) || 0;
        const white1Value = Number(dice.white1) || 0;
        const white2Value = Number(dice.white2) || 0;
        const white1Sum = white1Value + colorDieValue;
        const white2Sum = white2Value + colorDieValue;
        let combinedValue;

        // Check if dice are selected (existing flow) or allow direct marking (new flow)
        if (!selectedWhiteDie || selectedColor !== row) {
          // DIRECT MARKING: Check if clicked number matches any valid combination
          const clickedNumber = Number(number);
          const sum1 = Number(white1Sum);
          const sum2 = Number(white2Sum);
          if (clickedNumber === sum1) {
            combinedValue = sum1;
          } else if (clickedNumber === sum2) {
            combinedValue = sum2;
          } else {
            // Invalid combination - show simple error message
            setMessage(t('messages.invalidColorCombination'));
            setTimeout(() => setMessage(''), 4000);
            return;
          }
        } else {
          // EXISTING FLOW: Dice are selected
          const whiteDieValue = selectedWhiteDie === 1 ? dice.white1 : dice.white2;
          combinedValue = whiteDieValue + colorDieValue;

          // Check if the selected number matches the combined dice value
          if (number !== combinedValue) {
            setMessage(t('messages.invalidColorCombination'));
            setTimeout(() => setMessage(''), 3000);
            return;
          }
        }
        
        // Check directional rules
        const isValid = isValidNumberPlacement(row, number);
        if (!isValid) {
          setMessage(`You cannot mark ${number} in the ${row} row due to sequence rules`);
          setTimeout(() => setMessage(''), 3000);
          return;
        }
        
        // Find the element being marked and add animation
        const numberElement = document.querySelector(`.row.${row} .number:nth-child(${getNumberIndex(row, number)})`);
        if (numberElement) {
          numberElement.classList.add('marking');
          setTimeout(() => numberElement.classList.remove('marking'), 300);
        }
        
        // All checks passed, mark the number
        const newScorecard = { ...scorecard };
        if (!newScorecard[row].includes(number)) {
          newScorecard[row] = [...newScorecard[row], number].sort((a, b) => {
            return row === 'red' || row === 'yellow' ? a - b : b - a;
          });
        }
        setScorecard(newScorecard);
        
        // Update turn actions
        setTurnActions({ ...turnActions, colorDiceMarked: true });
        
        if (socket.connected) {
          socket.emit('markNumber', { 
            gameId, 
            playerName, 
            row, 
            number, 
            actionType: 'colorDice',
            whiteDie: selectedWhiteDie,
            colorDie: selectedColor
          });
          
          // Check if this was the last number in the row and should be closed
          // Pass 'colorDice' as the action type and the updated scorecard
          checkRowClosure(row, newScorecard[row], 'colorDice', newScorecard);
          
          // Auto-end turn after marking color dice
          console.log('[DEBUG] Setting autoEndingTurn=true');
          setAutoEndingTurn(true);
          
          setTimeout(() => {
            socket.emit('endTurn', { 
              gameId, 
              playerName
            });
            
            // Reset selections
            setSelectedWhiteDie(null);
            setSelectedColor(null);
            
            // Reset the auto-ending flag after a short delay to ensure
            // the turn has completely ended before allowing new actions
            setTimeout(() => {
              console.log('[DEBUG] Setting autoEndingTurn=false');
              setAutoEndingTurn(false);
            }, 500);
          }, 800); // Small delay for visual feedback
        }
        
        // Set message for player
        setMessage(t('messages.colorDiceMarked'));
        
        return;
      }
    }
  };
  
  // Helper to check if a number placement is valid based on row direction rules
  const isValidNumberPlacement = (row, number) => {
    const currentRow = scorecard[row];
    
    // No numbers in the row yet, any valid placement is fine
    if (currentRow.length === 0) return true;
    
    // For red and yellow rows (ascending order)
    if (row === 'red' || row === 'yellow') {
      // Must mark left to right, so new number must be greater than highest marked
      const highestMarked = Math.max(...currentRow);
      return number > highestMarked;
    }
    
    // For green and blue rows (descending order)
    if (row === 'green' || row === 'blue') {
      // Must mark right to left, so new number must be less than lowest marked
      const lowestMarked = Math.min(...currentRow);
      return number < lowestMarked;
    }
    
    return false;
  };
  
  // Helper to check if a row should be closed after marking a number
  const checkRowClosure = (row, markedNumbers, actionType = 'colorDice', updatedScorecard = null) => {
    // Early return if the row is already closed - prevents duplicate messages
    if (closedRows[row]) {
      console.log(`[DEBUG] Row ${row} is already closed, skipping closure check`);
      return;
    }

    // Check if we marked the last number (12 for red/yellow, 2 for green/blue)
    const lastNumber = row === 'red' || row === 'yellow' ? 12 : 2;

    // Check if the last number is marked and we have at least 6 numbers marked (5 regular + last number)
    if (markedNumbers.includes(lastNumber) && markedNumbers.length >= 6) {
      // Closure behavior depends on the action type and phase
      if (actionType === 'whiteSum') {
        // In white phase, don't close immediately but mark for pending closure
        console.log(`[DEBUG] Row ${row} marked for closure at the end of white phase`);
        console.log(`[DEBUG] Current pendingRowClosures before update:`, pendingRowClosures);
        
        // Create a new pendingRowClosures object to ensure state updates
        const newPendingClosures = {
          ...pendingRowClosures,
          [row]: true
        };
        
        console.log(`[DEBUG] New pendingRowClosures will be:`, newPendingClosures);
        setPendingRowClosures(newPendingClosures);
        
        // Add animation to the lock symbol to make it more obvious
        setTimeout(() => {
          const lockElement = document.querySelector(`.row.${row} .number.lock`);
          if (lockElement) {
            lockElement.classList.add('pending-marked');
            // This class will be removed when the row is actually closed
          }
          
          // Also mark the last number visually for THIS player
          const numberElement = document.querySelector(`.row.${row} .number:nth-child(${getNumberIndex(row, lastNumber)})`);
          if (numberElement) {
            numberElement.classList.add('marked');
          }
        }, 100);
        
        // Don't update the closedRows state yet - will be done at the end of white phase
        // Just notify the user that the row will be closed
        setMessage(`The ${row} row will be closed at the end of this round.`);
      } else if (actionType === 'colorDice') {
        // In color phase, close immediately (only active player can do this)
        console.log(`[DEBUG] Row ${row} closed immediately in color phase`);
        
        // Close the row
        const newClosedRows = {
          ...closedRows,
          [row]: true
        };
        
        setClosedRows(newClosedRows);
        
        // Remove the die from play
        setActiveDice({
          ...activeDice,
          [row]: false
        });
        
        // Add animation to both the lock symbol and the last number
        setTimeout(() => {
          // Animate the lock
          const lockElement = document.querySelector(`.row.${row} .number.lock`);
          if (lockElement) {
            // First reset any existing animations
            lockElement.classList.remove('marking');
            lockElement.classList.remove('pending-marked');
            
            // Add crossed class to ensure it appears crossed out (not filled)
            lockElement.classList.add('crossed');
            
            // Then add marking animation
            lockElement.classList.add('marking');
            setTimeout(() => {
              lockElement.classList.remove('marking');
            }, 500);
          }
          
          // Also animate the last number that closed the row - only for the active player
          const numberElement = document.querySelector(`.row.${row} .number:nth-child(${getNumberIndex(row, lastNumber)})`);
          if (numberElement) {
            numberElement.classList.add('marking');
            // Ensure it appears as marked
            numberElement.classList.add('marked');
            setTimeout(() => {
              numberElement.classList.remove('marking');
            }, 500);
          }
        }, 100);
        
        // Force a recomputation of the score to reflect the closure
        // Use updatedScorecard if provided (to avoid state timing issues), otherwise use current scorecard state
        const scorecardToUse = updatedScorecard || scorecard;
        const newScore = calculateScore(scorecardToUse, newClosedRows);
        console.log('[DEBUG] Updated score after color phase row closure:', newScore);
        console.log('[DEBUG] Used scorecard:', scorecardToUse);
        
        // IMPORTANT: Notify the server about the closed row BEFORE checking game end conditions
        // This ensures the server has the updated state when we check for game end
        if (socket && socket.connected) {
          console.log(`[DEBUG] Notifying server about immediate row closure: ${row}`);
          socket.emit('closeRow', { gameId, playerName, row });

          // Also notify about score update to ensure all clients have consistent scoring
          socket.emit('updateScore', {
            gameId,
            playerName,
            score: newScore.score,
            scorecard: scorecardToUse,
            closedRows: newClosedRows
          });
        }
        
        // Count ALL closed rows, including this new one
        const closedRowCount = Object.values(newClosedRows).filter(closed => closed).length;
        console.log(`[DEBUG] Total closed rows after this closure: ${closedRowCount}`);
        
        if (closedRowCount >= 2) {
          console.log('[DEBUG] Two or more rows closed - waiting for server to end game');
          // Do not set game status or emit gameEnd - wait for server to handle it
          // The server will receive the closeRow event and broadcast gameEnd if appropriate
        } else {
          setMessage(`The ${row} row is now closed and its die removed from play.`);
          setTimeout(() => setMessage(''), 3000);
        }
      }
    }
  };

  // Function to skip marking white dice
  const handleSkipWhiteDice = () => {
    console.log('[DEBUG] handleSkipWhiteDice called. Current state:', {
      turnPhase,
      currentPlayer,
      playerName,
      whiteSumMarked: turnActions.whiteSumMarked,
      whiteSumSkipped: turnActions.whiteSumSkipped
    });
    
    // Only the active player can skip white dice
    if (currentPlayer !== playerName) {
      setMessage(t('messages.activePlayerOnly', { action: t('buttons.skipWhiteDice').toLowerCase() }));
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Can only skip in whiteSum phase
    if (turnPhase !== 'whiteSum') {
      setMessage(t('messages.initialPhaseOnly'));
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    console.log(`[DEBUG] Active player ${playerName} skipping white dice`);
    
    // Update turn actions
    setTurnActions({ 
      ...turnActions, 
      whiteSumSkipped: true,
      whiteSumMarked: false  // Explicitly set to false when skipping
    });
    
    // Update local white phase completion tracking immediately to prevent button reappearing
    setPlayerWhitePhaseComplete(prev => {
      const newState = { ...prev, [playerName]: true };
      console.log('[DEBUG] Updated playerWhitePhaseComplete:', newState);
      return newState;
    });
    
    // Update the server if connected
    if (socket.connected) {
      // Tell server player is skipping white dice
      socket.emit('skipWhiteDice', { 
        gameId, 
        playerName
      });
      
      // Also explicitly tell server we've completed white phase
      socket.emit('playerCompletedWhitePhase', { 
        gameId, 
        playerName,
        action: 'skip'
      });
    }
    
    // Set message for the player
    setMessage(t('messages.whiteDiceSkipped'));
  };
  
  // Dedicated function to take a penalty
  const handleTakePenalty = () => {
    console.log('[DEBUG] handleTakePenalty called. Current state:', {
      turnPhase,
      currentPlayer,
      playerName,
      whiteSumMarked: turnActions.whiteSumMarked,
      whiteSumSkipped: turnActions.whiteSumSkipped,
      colorDiceMarked: turnActions.colorDiceMarked,
      penalties: scorecard.penalties
    });
    
    // Only the active player can take penalties
    if (currentPlayer !== playerName) {
      setMessage('Only the active player can take penalties');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Force color dice phase if not already in it - helps with edge cases
    if (turnPhase !== 'colorDice' && turnPhase !== 'complete') {
      console.log('[DEBUG] Forcing phase to colorDice for penalty handling');
      setTurnPhase('colorDice');
    }
    
    // Max 4 penalties
    if (scorecard.penalties >= 4) {
      setMessage('You already have 4 penalties (maximum)');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Add animation to the penalty being marked
    const penaltyElement = document.querySelector(`.row.penalties .number:nth-child(${scorecard.penalties + 2})`);
    if (penaltyElement) {
      penaltyElement.classList.add('marking');
      setTimeout(() => penaltyElement.classList.remove('marking'), 300);
    }
    
    // Reset any selected dice - fixes the issue of dice staying visually marked
    setSelectedWhiteDie(null);
    setSelectedColor(null);
    
    // Add a penalty
    const newScorecard = { ...scorecard, penalties: scorecard.penalties + 1 };
    setScorecard(newScorecard);
    
    if (socket.connected) {
      socket.emit('takePenalty', { gameId, playerName });
    }
    
    // Check if this is the 4th penalty (game end)
    if (newScorecard.penalties >= 4) {
      setMessage('Game over! You reached 4 penalties.');
      setGameStatus('finished');
      // Remove client-side gameEnd emission - let the server handle it
      // The server will broadcast the game end event to all players
      return;
    }
    
    // After marking a penalty, the active player's turn is complete
    setTurnPhase('complete');
    setMessage('Penalty taken. Your turn will end automatically.');
    
    // Mark both phases as complete to enable turn ending and ensure proper state
    // Make sure we recognize this action for future state checks
    setPlayerWhitePhaseComplete(prev => ({
      ...prev,
      [playerName]: true
    }));
    
    setTurnActions({
      whiteSumMarked: false,
      colorDiceMarked: false,
      whiteSumSkipped: true
    });
    
    // Auto-end turn after a short delay
    if (socket.connected) {
      console.log('[DEBUG] Setting autoEndingTurn=true after taking penalty');
      setAutoEndingTurn(true);
      
      setTimeout(() => {
        socket.emit('endTurn', { 
          gameId, 
          playerName,
          turnActions: {
            whiteSumMarked: false,
            colorDiceMarked: false,
            whiteSumSkipped: true
          }
        });
        
        // Reset the auto-ending flag after a short delay
        setTimeout(() => {
          console.log('[DEBUG] Setting autoEndingTurn=false');
          setAutoEndingTurn(false);
        }, 500);
      }, 1000); // Give user time to see the penalty
    }
  };

  // Function to end the current turn
  const handleEndTurn = () => {
    console.log('[DEBUG] handleEndTurn called. Current state:', {
      turnPhase,
      currentPlayer,
      playerName,
      whiteSumMarked: turnActions.whiteSumMarked,
      whiteSumSkipped: turnActions.whiteSumSkipped,
      colorDiceMarked: turnActions.colorDiceMarked,
      playerWhitePhaseComplete: playerWhitePhaseComplete[playerName]
    });
    
    // Only the active player can end their turn
    if (currentPlayer !== playerName) {
      setMessage('Only the active player can end their turn');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Can only end turn in the colorDice or complete phase
    if (turnPhase !== 'colorDice' && turnPhase !== 'complete') {
      setMessage('You must first decide whether to mark using the white sum');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // If player has skipped white dice marking and hasn't marked a color die yet,
    // they should take a penalty instead of ending their turn
    if (turnPhase === 'colorDice' && 
        turnActions.whiteSumSkipped && 
        !turnActions.colorDiceMarked) {
      setMessage('You must take a penalty when you skip marking white dice');
      setTimeout(() => setMessage(''), 3000);
      // Automatically trigger the penalty button
      handleTakePenalty();
      return;
    }
    
    // In colorDice phase, normally we'd require an action, but we'll be more permissive 
    // to handle edge cases where button state might be inconsistent
    if (turnPhase === 'colorDice' && 
        !turnActions.whiteSumMarked && 
        !turnActions.colorDiceMarked && 
        !turnActions.whiteSumSkipped && 
        !playerWhitePhaseComplete[playerName]) {
      console.log('[DEBUG] Warning: Ending turn without a recorded action, but proceeding anyway:', turnActions);
      // We'll show a message but proceed with the turn end anyway
      setMessage('Ending your turn...');
    } else {
      console.log('[DEBUG] Ending turn normally...');
    }
    
    if (socket.connected) {
      // Clear selected dice for the next player
      setSelectedWhiteDie(null);
      setSelectedColor(null);
      
      // Force the whiteSumSkipped to true if we're in colorDice phase and no white sum action
      // was recorded - this ensures the server recognizes the phase completion
      const updatedTurnActions = { ...turnActions };
      if (turnPhase === 'colorDice' && !updatedTurnActions.whiteSumMarked && !updatedTurnActions.whiteSumSkipped) {
        console.log('[DEBUG] Forcing whiteSumSkipped=true to ensure proper phase handling');
        updatedTurnActions.whiteSumSkipped = true;
      }
      
      // Tell server to end turn
      socket.emit('endTurn', { 
        gameId, 
        playerName,
        turnActions: updatedTurnActions
      });
      
      // Clear guidance
      setShowGuidance(false);
    }
  };
  
  // Helper function to get the next player
  // eslint-disable-next-line no-unused-vars
  const getNextPlayer = (playersList, currentPlayerName) => {
    const currentIndex = playersList.findIndex(player => player.name === currentPlayerName);
    if (currentIndex === -1) return playersList[0].name;
    
    const nextIndex = (currentIndex + 1) % playersList.length;
    return playersList[nextIndex].name;
  };

  // Unified sharing functionality (shareStatus is now from useGameState hook)

  // Generate beautiful formatted leaderboard text for clipboard
  const generateLeaderboardText = () => {
    const playerScoreEntries = Object.entries(allPlayerScores);

    if (playerScoreEntries.length === 0) return '';

    // Sort players by score (highest first), handling both old and new format
    const sortedPlayers = playerScoreEntries
      .sort(([, scoreDataA], [, scoreDataB]) => {
        const scoreA = typeof scoreDataA === 'number' ? scoreDataA : scoreDataA.score;
        const scoreB = typeof scoreDataB === 'number' ? scoreDataB : scoreDataB.score;
        return scoreB - scoreA;
      });

    // Build the leaderboard text with language-specific formatting
    let leaderboardText = `${t('gameOver.leaderboardTitle')}\n\n`;

    sortedPlayers.forEach(([name, scoreData], index) => {
      const position = index + 1;
      const medal = position === 1 ? '🥇' : position === 2 ? '🥈' : position === 3 ? '🥉' : '🏅';
      const isCurrentPlayer = name === playerName ? ' (me)' : '';
      // Extract score from either format
      const score = typeof scoreData === 'number' ? scoreData : scoreData.score;
      leaderboardText += `${medal} ${position}. ${name}${isCurrentPlayer}: ${score} points\n`;
    });

    // Add challenge text and play now link
    leaderboardText += `\n${t('gameOver.challengeText')} ${t('gameOver.playNowText')}`;

    return leaderboardText;
  };

  // Unified Facebook share function - copies leaderboard and opens Facebook
  const handleUnifiedFacebookShare = async () => {
    try {
      // First, copy the formatted leaderboard to clipboard
      const leaderboardText = generateLeaderboardText();
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(leaderboardText);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = leaderboardText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      // Track the share usage on server
      try {
        const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:4001';
        await fetch(`${serverUrl}/api/track-share`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent
          })
        });
        console.log('Share tracked successfully');
      } catch (trackError) {
        console.log('Failed to track share (non-critical):', trackError);
        // Don't let tracking errors affect the user experience
      }
      
      // Show success message
      setShareStatus(t('gameOver.shareSuccess'));

      // Then open Facebook sharing
      const gameUrl = 'https://qwixx-online.nl';
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(gameUrl)}`;

      openExternalUrl(facebookUrl);
      
    } catch (error) {
      console.error('Share failed:', error);
      setShareStatus(t('gameOver.shareError'));
    }
    
    // Clear status message after 5 seconds (longer so user can read it)
    setTimeout(() => setShareStatus(''), 5000);
  };

  // Function to calculate white dice sum
  // eslint-disable-next-line no-unused-vars
  const getWhiteDiceSum = () => {
    if (dice.white1 && dice.white2) {
      return dice.white1 + dice.white2;
    }
    return null;
  };

  // Function to calculate combined value (white + colored)
  // eslint-disable-next-line no-unused-vars
  const getCombinedValue = () => {
    if (selectedWhiteDie !== null && selectedColor) {
      const whiteDieValue = selectedWhiteDie === 1 ? dice.white1 : dice.white2;
      const colorDieValue = dice[selectedColor];
      if (whiteDieValue && colorDieValue) {
        return whiteDieValue + colorDieValue;
      }
    }
    return null;
  };

  // Render a scorecard row
  // eslint-disable-next-line no-unused-vars
  const renderScoreRow = (color, numbers) => {
    return (
      <div className="row">
        <div className={`row-header ${color}`}>
          {color.charAt(0).toUpperCase() + color.slice(1)}
        </div>
        <div className="row-numbers">
          {numbers.map(num => (
            <div
              key={`${color}-${num}`}
              className={`number-cell ${scorecard[color].includes(num) ? 'marked' : ''}`}
              onClick={() => handleMarkNumber(color, num)}
            >
              {num}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Calculate score for the player
  const calculateScore = useCallback((playerScorecard = scorecard, playerClosedRows = closedRows, playerRowClosers = rowClosers, currentPlayerName = playerName) => {
    let score = 0;
    const breakdown = {
      red: 0,
      yellow: 0,
      green: 0,
      blue: 0,
      penalties: 0,
      total: 0
    };

    // Add scores for each row
    ['red', 'yellow', 'green', 'blue'].forEach(color => {
      // Base count of marked numbers in the row - CRITICAL: Check count FIRST
      const count = playerScorecard[color].length;

      // Early exit if no marks - prevents phantom points bug
      if (count === 0) {
        breakdown[color] = 0;
        return; // Skip to next color
      }

      // Check if the row is closed
      // Lock bonus is ONLY given to players who are in the rowClosers array for this color
      const lastNumber = color === 'red' || color === 'yellow' ? 12 : 2;

      // Debug logging to trace the issue
      const hasLastNumber = playerScorecard[color].includes(lastNumber);
      const hasEnoughMarks = count >= 6;
      const didPlayerCloseRow = playerRowClosers[color]?.includes(currentPlayerName) || false;

      console.log(`[CLIENT] [SCORE] DEBUG Row ${color}:`, {
        playerName: currentPlayerName,
        markedNumbers: playerScorecard[color],
        count: count,
        lastNumber: lastNumber,
        hasLastNumber: hasLastNumber,
        hasEnoughMarks: hasEnoughMarks,
        rowClosers: playerRowClosers[color] || [],
        didPlayerCloseRow: didPlayerCloseRow
      });

      // Only give lock bonus if this player actually closed the row
      const isRowClosed = didPlayerCloseRow && hasEnoughMarks;

      // Calculate actual count including lock bonus (only after validating count > 0)
      const actualCount = isRowClosed ? count + 1 : count;
      const rowScore = (actualCount * (actualCount + 1)) / 2; // Sum formula: n(n+1)/2

      score += rowScore;
      breakdown[color] = rowScore;

      if (isRowClosed) {
        console.log(`[CLIENT] [SCORE] Row ${color}: ${count} marks (including last number) + 1 lock bonus = ${actualCount} total = ${rowScore} points`);
      } else {
        console.log(`[CLIENT] [SCORE] Row ${color}: ${count} marks = ${rowScore} points (NOT CLOSED)`);
      }
    });

    // Subtract penalties
    const penaltyPoints = playerScorecard.penalties * 5;
    score -= penaltyPoints;
    breakdown.penalties = -penaltyPoints;
    breakdown.total = score;

    console.log(`[CLIENT] [SCORE] Total: ${score} points (red=${breakdown.red}, yellow=${breakdown.yellow}, green=${breakdown.green}, blue=${breakdown.blue}, penalties=${breakdown.penalties})`);

    return { score, breakdown };
  }, [scorecard, closedRows, rowClosers, playerName]);

  // Function to handle starting a new game with the same players
  const handleStartNewGame = () => {
    console.log('[DEBUG] Attempting to start a new game...');
    
    // Reset game state
    setDice({
      white1: null,
      white2: null,
      red: null,
      yellow: null,
      green: null,
      blue: null
    });
    
    setScorecard({
      red: [],
      yellow: [],
      green: [],
      blue: [],
      penalties: 0
    });
    
    setClosedRows({
      red: false,
      yellow: false,
      green: false,
      blue: false
    });

    setRowClosers({
      red: [],
      yellow: [],
      green: [],
      blue: []
    });

    // Clear any active row closure overlays
    setRowClosureOverlays({
      red: false,
      yellow: false,
      green: false,
      blue: false
    });
    
    // Clear any pending timeouts
    Object.keys(overlayTimeoutsRef.current).forEach(row => {
      if (overlayTimeoutsRef.current[row]) {
        clearTimeout(overlayTimeoutsRef.current[row]);
        overlayTimeoutsRef.current[row] = null;
      }
    });
    
    setActiveDice({
      red: true,
      yellow: true,
      green: true,
      blue: true
    });
    
    // Reset other game state
    setTurnPhase('initial');
    setGameStatus('waiting');
    setPendingRowClosures({
      red: false,
      yellow: false,
      green: false,
      blue: false
    });
    
    // Emit event to server if connected
    if (socket && socket.connected) {
      const isHost = players.find(p => p.name === playerName)?.isHost;
      
      // Only send the restartGame event if you're the host
      if (isHost) {
        socket.emit('restartGame', { gameId, playerName }, (response) => {
          if (response && !response.success) {
            console.error('Error restarting game:', response.error);
            setMessage(t('errors.restartFailed', { error: response.error }));
          }
        });
        setMessage(t('messages.gameRestarting'));
      } else {
        // If not host, show an error message
        setMessage(t('errors.hostOnly'));
      }
    } else {
      setMessage(t('errors.cannotRestart'));
    }
  };

  // Function to handle dice selection for marking with color dice
  const handleSelectDie = (dieType, dieNumber) => {
    // Can only select dice in the colorDice phase
    if (turnPhase !== 'colorDice') {
      setMessage('You can only select dice after rolling and deciding about the white sum');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Only the active player can select dice
    if (currentPlayer !== playerName) {
      setMessage('Only the active player can select dice');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Can't select dice if the player already marked a number using colored dice
    if (turnActions.colorDiceMarked) {
      setMessage('You have already marked a number using colored dice this turn');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Handle white die selection
    if (dieType === 'white') {
      const newSelectedWhiteDie = selectedWhiteDie === dieNumber ? null : dieNumber;
      setSelectedWhiteDie(newSelectedWhiteDie);
      
      if (newSelectedWhiteDie) {
        setMessage(t('dice.dieSelected', { color: 'white' + dieNumber }));
        
        // If color is already selected, show possible combination
        if (selectedColor) {
          const whiteDieValue = newSelectedWhiteDie === 1 ? dice.white1 : dice.white2;
          const colorDieValue = dice[selectedColor];
          const sum = whiteDieValue + colorDieValue;
          
          setMessage(t('messages.whiteSumCombined', {
            die: newSelectedWhiteDie,
            value: whiteDieValue,
            color: selectedColor,
            colorValue: colorDieValue,
            sum: sum
          }));
        }
      } else {
        setMessage(''); // Just clear the message instead of showing an unnecessary status
      }
      return;
    }
    
    // Handle color die selection
    // Toggle selection if same die is clicked
    const newSelectedColor = selectedColor === dieType ? null : dieType;
    
    // Check if the row is closed
    if (newSelectedColor && closedRows[dieType]) {
      setMessage(`The ${dieType} row is closed and its die cannot be used`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Check if the die is active
    if (newSelectedColor && !activeDice[dieType]) {
      setMessage(`The ${dieType} die is not active in this game`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Set the selected color
    setSelectedColor(newSelectedColor);
    
    if (!newSelectedColor) {
      setMessage(''); // Just clear the message instead of showing a status
      return;
    }
    
    // If a white die is already selected, calculate the possible number
    if (selectedWhiteDie) {
      const whiteDieValue = selectedWhiteDie === 1 ? dice.white1 : dice.white2;
      const colorDieValue = dice[newSelectedColor];
      const sum = whiteDieValue + colorDieValue;
      
      setMessage(t('messages.whiteSumCombined', {
        die: selectedWhiteDie,
        value: whiteDieValue,
        color: newSelectedColor,
        colorValue: colorDieValue,
        sum: sum
      }));
    } else {
      setMessage(t('dice.dieSelected', { color: newSelectedColor }));
    }
  };

  // Helper to get index of a number in the row for animation
  const getNumberIndex = useCallback((row, number) => {
    // For red and yellow rows (2-12)
    if (row === 'red' || row === 'yellow') {
      // Adjust for the row header and 0-based index of the numbers starting from 2
      return number - 2 + 2; // +1 for the header, and +1 to adjust from 0-base to 1-base
    }
    // For green and blue rows (12-2)
    if (row === 'green' || row === 'blue') {
      // Find position in the descending array [12,11,10,...,2]
      const position = 12 - number;
      return position + 2; // +1 for the header, and +1 to adjust from 0-base to 1-base
    }
    return 2; // Default fallback
  }, []);

  // Add a function to close the guidance modal
  // eslint-disable-next-line no-unused-vars
  const closeGuidance = () => {
    setShowGuidance(false);
  };

  // Add a helper function to determine if a number should be highlighted for marking
  // eslint-disable-next-line no-unused-vars
  const shouldHighlightNumber = (row, number) => {
    // Only highlight in the whiteSum phase
    if (turnPhase !== 'whiteSum') return false;
    
    // Don't highlight if player already marked a number using white dice this turn
    if (turnActions.whiteSumMarked || turnActions.whiteSumSkipped) return false;
    
    // Only highlight if the dice have been rolled
    if (!dice.white1 || !dice.white2) return false;
    
    // Check if the number matches the white dice sum
    const whiteSum = dice.white1 + dice.white2;
    if (number !== whiteSum) return false;
    
    // Don't highlight marked numbers
    if (scorecard[row].includes(number)) return false;
    
    // Check directional rules (left-to-right for red/yellow, right-to-left for green/blue)
    const isValid = isValidNumberPlacement(row, number);
    if (!isValid) return false;
    
    // All conditions passed, highlight this number
    return true;
  };

  // Helper to check if a player can mark white sum right now
  // eslint-disable-next-line no-unused-vars
  const canMarkWhiteSum = () => {
    // Only in whiteSum phase
    if (turnPhase !== 'whiteSum') return false;
    
    // Don't allow if already marked
    if (turnActions.whiteSumMarked || turnActions.whiteSumSkipped) return false;
    
    return true;
  };
  
  // Helper to show guidance for non-active players
  useEffect(() => {
    // Show guidance for non-active players when white sum is available
    if (turnPhase === 'whiteSum' && currentPlayer !== playerName && !turnActions.whiteSumMarked) {
      setGuidanceStep('whiteSum');
      setShowGuidance(true);
    }
  }, [turnPhase, currentPlayer, playerName, turnActions.whiteSumMarked, setGuidanceStep, setShowGuidance]);

  // Add a function for non-active players to skip white dice marking
  const handleNonActiveSkipWhite = () => {
    console.log('[DEBUG] handleNonActiveSkipWhite called. Current state:', {
      turnPhase,
      currentPlayer,
      playerName,
      whiteSumMarked: turnActions.whiteSumMarked,
      whiteSumSkipped: turnActions.whiteSumSkipped
    });
    
    // Only non-active players in the whiteSum phase can use this
    if (currentPlayer === playerName || turnPhase !== 'whiteSum') {
      return;
    }
    
    console.log(`[DEBUG] Non-active player ${playerName} skipping white dice`);
    
    // Update local turn actions to indicate skipping white dice
    setTurnActions({
      ...turnActions,
      whiteSumSkipped: true,
      whiteSumMarked: false
    });
    
    // Update local white phase completion tracking immediately to prevent button reappearing
    setPlayerWhitePhaseComplete(prev => {
      const newState = { ...prev, [playerName]: true };
      console.log('[DEBUG] Updated playerWhitePhaseComplete:', newState);
      return newState;
    });
    
    // Notify server if connected
    if (socket.connected) {
      // Tell server player is skipping white dice
      socket.emit('skipWhiteDice', { 
        gameId, 
        playerName
      });
      
      // Also explicitly tell server we've completed white phase
      socket.emit('playerCompletedWhitePhase', { 
        gameId, 
        playerName,
        action: 'skip'
      });
    }
    
    // Set message for the player
    setMessage(t('messages.nonActiveSkipped'));
  };

  // Function to handle first player selection with clearer notification
  const handleSelectFirstPlayer = (selectedPlayerName) => {
    console.log(`Selecting first player: ${selectedPlayerName}`);
    
    // Update local state
    setCurrentPlayer(selectedPlayerName);
    setShowFirstPlayerModal(false);
    setGameInitialized(true); // Immediately set as initialized to prevent modal from reopening
    
    // Set a clear message for everyone using translations
    setMessage(t('firstPlayerSelection.playerSelected', { player: selectedPlayerName }));
    setTimeout(() => setMessage(''), 5000);
    
    // If we have a socket connection, emit the selection to the server
    if (socket.connected) {
      console.log(`Emitting selectFirstPlayer event for ${selectedPlayerName}`);
      socket.emit('selectFirstPlayer', { 
        gameId, 
        player: selectedPlayerName 
      });
    }
  };

  // Function to randomly select the first player
  const handleRandomFirstPlayer = () => {
    // Start spinning animation
    setSpinningWheel(true);
    
    // Get all available players
    const availablePlayers = players.filter(p => !p.disconnected);
    
    // Short delay for animation effect
    setTimeout(() => {
      // Randomly select a player
      const randomIndex = Math.floor(Math.random() * availablePlayers.length);
      const randomPlayer = availablePlayers[randomIndex].name;
      
      // Stop spinning animation
      setSpinningWheel(false);
      
      // Select the player
      handleSelectFirstPlayer(randomPlayer);
    }, 1500); // Increased from 1000 to 1500ms for a more noticeable effect
  };

  // Chat state (showChat and unreadMessages are now from useGameState hook)

  // Functions to handle rules modal
  const openRulesModal = () => {
    setShowRulesModal(true);
  };
  
  const closeRulesModal = () => {
    setShowRulesModal(false);
  };

  // Handler for zooming the scorecard
  const handleZoom = (direction) => {
    setScorecardZoom(prevZoom => {
      const newZoom = direction === 'in' 
        ? Math.min(prevZoom + 0.1, 1.8)  // Maximum zoom of 1.8x
        : Math.max(prevZoom - 0.1, 1.0);  // Minimum zoom of 1.0x (100%)
      return parseFloat(newZoom.toFixed(1)); // Keep to 1 decimal place
    });
  };
  
  // Reset zoom when game changes
  useEffect(() => {
    setScorecardZoom(1);
  }, [gameId, setScorecardZoom]);

  // Handle player quitting the game gracefully
  const handleQuitGame = () => {
    // Only emit the event if we have a socket connection
    if (socket && socket.connected) {
      // Notify other players that this player is quitting
      socket.emit('playerQuit', { 
        gameId, 
        playerName,
        message: t('messages.playerQuit', { playerName })
      });
      
      // Small delay to ensure the message is sent before disconnecting
      setTimeout(() => {
        onEndGame();
      }, 100);
    } else {
      // If no socket connection, just exit directly
      onEndGame();
    }
  };

  // Function to finalize any pending row closures at the end of the white phase
  // Wrap in useCallback to prevent ESLint dependency warning
  const finalizePendingRowClosures = useCallback(() => {
    console.log('[DEBUG] Checking for pending row closures to finalize:', pendingRowClosures);
    console.log('[DEBUG] Current closedRows state:', closedRows);
    
    // Check if there are any pending closures
    const hasPendingClosures = Object.values(pendingRowClosures).some(pending => pending);
    if (!hasPendingClosures) {
      console.log('[DEBUG] No pending row closures to finalize');
      return;
    }
    
    // Get the rows that need to be closed
    const rowsToClose = Object.keys(pendingRowClosures).filter(row => pendingRowClosures[row]);
    
    if (rowsToClose.length > 0) {
      console.log('[DEBUG] Finalizing pending row closures for:', rowsToClose);
      
      // Create a new closedRows object with all pending closures set to true
      const newClosedRows = { ...closedRows };
      const newActiveDice = { ...activeDice };
      
      rowsToClose.forEach(row => {
        newClosedRows[row] = true;
        newActiveDice[row] = false;
        
        // Get the last number for this row
        const lastNumber = (row === 'red' || row === 'yellow') ? 12 : 2;
        
        // Add animation to the lock symbol and last number ONLY for the player who closed the row
        // In white dice phase, the current player is closing the row if they marked the last number
        // so we check if the last number is in their scorecard
        setTimeout(() => {
          // Only animate and mark the lock if the player has marked the row's last number
          if (scorecard[row].includes(lastNumber)) {
            const lockElement = document.querySelector(`.row.${row} .number.lock`);
            if (lockElement) {
              // Remove the pending-marked class
              lockElement.classList.remove('pending-marked');
              
              // Add crossed class to make sure it shows as crossed out (not filled)
              lockElement.classList.add('crossed');
              
              // Add marking animation
              lockElement.classList.add('marking');
              setTimeout(() => {
                lockElement.classList.remove('marking');
              }, 500);
            }
            
            // Animate and mark the last number that closed the row
            const lastNumberElement = document.querySelector(`.row.${row} .number:nth-child(${getNumberIndex(row, lastNumber)})`);
            if (lastNumberElement) {
              lastNumberElement.classList.add('marking');
              // Ensure it appears as marked
              lastNumberElement.classList.add('marked');
              setTimeout(() => {
                lastNumberElement.classList.remove('marking');
              }, 500);
            }
          }
        }, 100);
      });
      
      console.log('[DEBUG] New closedRows state will be:', newClosedRows);
      
      // Update the state
      setClosedRows(newClosedRows);
      setActiveDice(newActiveDice);
      
      // Force a recomputation of the score to reflect the closure
      const newScore = calculateScore(scorecard, newClosedRows);
      console.log('[DEBUG] Updated score after row closure:', newScore);
      
      // Reset pending closures
      setPendingRowClosures({
        red: false,
        yellow: false,
        green: false,
        blue: false
      });
      
      // Notify the server about each closed row
      if (socket && socket.connected) {
        rowsToClose.forEach(row => {
          console.log(`[DEBUG] Notifying server about closed row: ${row}`);
          socket.emit('closeRow', { gameId, playerName, row });
          
          // Also notify about score update to ensure all clients have consistent scoring
          socket.emit('updateScore', { 
            gameId, 
            playerName, 
            score: newScore.score,
            scorecard: scorecard,
            closedRows: newClosedRows
          });
        });
      }
      
      // Check if this closes 2 or more rows (game end condition)
      // This needs to count ALL closed rows, including these new ones
      const closedRowCount = Object.values(newClosedRows).filter(closed => closed).length;
      console.log('[DEBUG] Total closed rows after finalization:', closedRowCount);
      
      if (closedRowCount >= 2) {
        console.log('[DEBUG] Two or more rows closed - waiting for server to end game');
        // We'll wait for the server to determine game end
        // The server will receive the closeRow events and broadcast gameEnd when appropriate
      } else {
        const rowNames = rowsToClose.map(row => t(`colors.${row}`)).join(' and ');
        setMessage(`The ${rowNames} ${rowsToClose.length > 1 ? 'rows are' : 'row is'} now closed.`);
        setTimeout(() => setMessage(''), 3000);
      }
    }
  }, [pendingRowClosures, closedRows, activeDice, scorecard, gameId, playerName, t, setMessage, setActiveDice, setClosedRows, setPendingRowClosures, calculateScore, getNumberIndex]);

  // Add a utility function to determine which button to show in the color phase
  // Add this at the end of the component, just before the final return statement
  // Render row closure overlays
  const renderRowClosureOverlays = () => {
    return Object.keys(rowClosureOverlays).map(row => {
      if (!rowClosureOverlays[row]) return null;
      
      const colorName = t(`colors.${row}`);
      
      return (
        <div className="row-closure-overlay" key={`row-closure-${row}`}>
          <div className={`row-closure-message ${row}`}>
            <h2>{colorName}</h2>
            <p>{t('messages.rowClosedOverlay', { color: colorName })}</p>
          </div>
        </div>
      );
    });
  };

  return (
    <div className={`game-page ${isLoading ? 'loading' : ''}`}>
      <div className="game-container">
        {isLoading ? (
          <div className="loading-screen">
            <h1>{t('loading.gameLoading')}</h1>
            <p>{t('loading.gameId', { gameId })}</p>
                </div>
        ) : connectionError ? (
          <div className="error-screen">
            <h1>{t('errors.connectionError')}</h1>
            <p>{t('errors.offlineMode')}</p>
            <p>{t('errors.tryRefresh')}</p>
            
            <div className="server-info">
              <h3>{t('errors.possibleSolutions')}</h3>
              <ul>
                <li>{t('errors.solution1')}</li>
                <li>{t('errors.solution2')}</li>
                <li>{t('errors.solution3')}</li>
                <li>{t('errors.solution4')}</li>
              </ul>
              <div>
                <strong>{t('common.serverUrl')}:</strong> {socket?.io?.uri || 'N/A'}
                </div>
                </div>
                </div>
        ) : gameStatus === 'finished' ? (
          <GameOverModal
            show={true}
            message={message}
            allPlayerScores={allPlayerScores}
            allPlayerScorecards={allPlayerScorecards}
            allPlayerClosedRows={allPlayerClosedRows}
            allPlayerRowClosers={allPlayerRowClosers}
            gameWinner={gameWinner}
            players={players}
            playerName={playerName}
            onPlayAgain={handleStartNewGame}
            onBackToHome={onEndGame}
            onOpenRules={openRulesModal}
            gameId={gameId}
            shareStatus={shareStatus}
            onFacebookShare={handleUnifiedFacebookShare}
          />
        ) : (
          <>
            {/* 3D Dice Header with Game ID inline */}
            <div className="dice-header" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '0', position: 'relative' }}>
              {/* Language selector - positioned like in Homepage */}
              <div style={{ 
                position: 'absolute',
                left: '-25px', 
                top: '-20px',
                transform: 'scale(0.625)',
                transformOrigin: 'center left',
                zIndex: 10 
              }}>
                <LanguageSelector />
              </div>
              
              {/* Audio and Information buttons - positioned opposite to language selector */}
              <div style={{
                position: 'absolute',
                right: '-15px',
                top: '-15px',
                zIndex: 10,
                display: 'flex',
                gap: '0.25rem',
                alignItems: 'center'
              }}>
                <AudioMuteButton style={{ fontSize: '1rem' }} />
                <button
                  className="info-button"
                  onClick={openRulesModal}
                  aria-label={t('gameRules.title')}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  i
                </button>
              </div>
              
              <div className="dice-container">
                <div 
                  className="die red"
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateY(360deg)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateY(0deg)'}
                >
                  <div className="die-face die-front one">
                    <div className="die-dots">
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-back six">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-top two">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-bottom five">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-left four">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-right three">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                </div>
                
                <div 
                  className="die yellow"
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateX(360deg)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateX(0deg)'}
                >
                  <div className="die-face die-front two">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-back five">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-top six">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-bottom one">
                    <div className="die-dots">
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-left three">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-right four">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                </div>
                
                <div 
                  className="die green"
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'rotateZ(360deg)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'rotateZ(0deg)'}
                >
                  <div className="die-face die-front three">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-back four">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-top one">
                    <div className="die-dots">
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-bottom six">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-left five">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-right two">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                </div>
                
                <div 
                  className="die blue"
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate3d(1, 1, 1, 360deg)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate3d(1, 1, 1, 0deg)'}
                >
                  <div className="die-face die-front four">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-back one">
                    <div className="die-dots">
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-top three">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-bottom two">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-left six">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                  <div className="die-face die-right five">
                    <div className="die-dots">
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                      <div className="dot"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Header Section */}
            <div className="game-header">
              <h1 style={{ 
                textAlign: 'center', 
                marginTop: '0.5rem',
                marginBottom: '0.5rem',
                background: 'linear-gradient(to right, #ef4444, #f59e0b, #10b981, #3b82f6)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
                fontSize: '2.5rem',
                fontWeight: '700',
                position: 'relative',
                display: 'inline-block',
                padding: '0 1rem',
                letterSpacing: '0.05em'
              }}>
                {t('gameTitle')}
              </h1>
              
              {/* Game message - Fixed height to prevent screen jumping */}
              <div className="message-banner" style={{
                minHeight: '3.5rem',  // Fixed minimum height for up to 2 lines of text
                marginTop: '0.5rem',
                padding: '0.75rem',
                backgroundColor: message ? '#fff0f0' : 'transparent',
                color: '#b91c1c',
                borderRadius: '0.375rem',
                fontWeight: '600',
                textAlign: 'center',
                border: message ? '2px solid #fca5a5' : '2px solid transparent',
                boxShadow: message ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none',
                fontSize: '1rem',
                maxWidth: '500px',
                width: '90%',
                margin: '0.5rem auto',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                overflow: 'hidden',
                hyphens: 'auto',
                lineHeight: '1.4',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                visibility: message ? 'visible' : 'hidden',  // Hide but preserve space
                opacity: message ? 1 : 0,  // Smooth fade effect
                transition: 'opacity 0.2s ease-in-out'  // Smooth transition
              }}>
                {message || '\u00A0'}  {/* Non-breaking space when empty to maintain height */}
              </div>
            </div>
            

            {/* Removed white-sum-notification message box */}

            {/* Players - Now using extracted component */}
            <PlayerList
              players={players}
              currentPlayer={currentPlayer}
              playerName={playerName}
            />
            
            {/* Dice Display - Now using extracted component */}
            <DiceDisplay
              dice={dice}
              selectedWhiteDie={selectedWhiteDie}
              selectedColor={selectedColor}
              activeDice={activeDice}
              turnPhase={turnPhase}
              currentPlayer={currentPlayer}
              playerName={playerName}
              onSelectDie={handleSelectDie}
            />
              
            {/* Turn Timer Display - reserve space when timer mode is enabled */}
            {turnTimerEnabled && (
              <div className={`turn-timer ${timerSecondsLeft !== null && timerSecondsLeft <= 10 ? 'warning' : ''} ${timerSecondsLeft === null ? 'hidden' : ''}`}>
                {timerSecondsLeft !== null ? `${timerSecondsLeft}s` : '--'}
              </div>
            )}

            {/* Turn Controls - Now using extracted component */}
            <TurnControls
              currentPlayer={currentPlayer}
              playerName={playerName}
              turnPhase={turnPhase}
              gameInitialized={gameInitialized}
              turnActions={turnActions}
              playerWhitePhaseComplete={playerWhitePhaseComplete}
              autoEndingTurn={autoEndingTurn}
              onRollDice={handleRollDice}
              onSkipWhiteDice={handleSkipWhiteDice}
              onNonActiveSkipWhite={handleNonActiveSkipWhite}
              onTakePenalty={handleTakePenalty}
              onEndTurn={handleEndTurn}
            />
            
            {/* Scorecard and LiveScore - Now using extracted components */}
            <Scorecard
              scorecard={scorecard}
              closedRows={closedRows}
              pendingRowClosures={pendingRowClosures}
              scorecardZoom={scorecardZoom}
              scorecardRef={scorecardRef}
              onMarkNumber={handleMarkNumber}
              onZoomIn={() => handleZoom('in')}
              onZoomOut={() => handleZoom('out')}
            />

            <LiveScore
              scorecard={scorecard}
              closedRows={closedRows}
              rowClosers={rowClosers}
              playerName={playerName}
            />

            {/* Opponent Scores - shown when enabled by host */}
            {console.log('[DEBUG] Render check - showOpponentScores:', showOpponentScores, 'allPlayerLiveScores keys:', Object.keys(allPlayerLiveScores))}
            {showOpponentScores && Object.keys(allPlayerLiveScores).length > 0 && (
              <OpponentScores
                allPlayerLiveScores={allPlayerLiveScores}
                currentPlayerName={playerName}
              />
            )}

            {/* Fixed position chat container - repositioned to ensure it stays under scorecard */}
            <div id="chat-container" style={{ 
              position: 'relative',
              width: '100%',
              maxWidth: '1000px',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '1rem',
              paddingRight: '20px',
              zIndex: 50
            }}>
              <GameChat 
                socket={socket}
                gameId={gameId}
                playerName={playerName}
                showChat={showChat}
                setShowChat={setShowChat}
                unreadCount={unreadMessages}
                setUnreadCount={setUnreadMessages}
              />
            </div>
            
            {/* Centered Quit Game button with Chat icon */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              margin: '1.5rem auto',
              width: '100%'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <button 
                  className="action-btn quit-game" 
                  onClick={handleQuitGame}
                  style={{ 
                    backgroundColor: '#ef4444',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.375rem',
                    border: 'none',
                    color: 'white',
                    fontWeight: '500',
                    cursor: 'pointer',
                    maxWidth: '200px'
                  }}
                >
                  {t('buttons.quitGame')}
                </button>
              </div>
            </div>
            
            {/* Game ID - Always visible for all players */}
            <div style={{ 
              textAlign: 'center', 
              margin: '0', /* Removed all margins */
              fontSize: '0.875rem', 
              color: '#6b7280',
              paddingTop: '0', /* No padding needed at the top */
              display: 'block', /* Ensure it's always displayed */
              visibility: 'visible' /* Explicitly make visible */
            }}>
              {t('common.gamePin', { gameId })}
            </div>
            
            {/* Copyright notice - removed chat component from here */}
            <div style={{ 
              textAlign: 'center', 
              margin: '0', /* Removed top margin */
              fontSize: '0.875rem', 
              color: '#6b7280',
              position: 'relative',
              paddingBottom: '0', /* Removed padding */
              paddingTop: '4px' /* Small padding for spacing */
            }}>
              © 2025 Qwixx Online
            </div>
            
            {/* Disclaimer */}
            <div className="disclaimer-container">
              <span className="disclaimer-trigger">{t('disclaimer.title')}</span>
              <div className="disclaimer-content">
                {t('disclaimer.content')}
              </div>
            </div>
          </>
        )}
        
        {/* First Player Selection Modal - Now using extracted component */}
        <FirstPlayerModal
          show={showFirstPlayerModal}
          players={players}
          currentPlayerName={playerName}
          onSelectPlayer={handleSelectFirstPlayer}
          onRandomSelect={handleRandomFirstPlayer}
          spinningWheel={spinningWheel}
        />
        
        {/* Rules Modal - Now using extracted component */}
        <RulesModal
          show={showRulesModal}
          onClose={closeRulesModal}
        />

        {/* Audio Feature Popup - Shown once per user for 1 month */}
        <AudioFeaturePopup />

      </div>
      
      {/* Add Row Closure Overlays */}
      {renderRowClosureOverlays()}
    </div>
  );
};

export default GamePage;